import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ConfirmationDialogResult, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as appReportService from "../../../models/services/appReportService";
import { ISessionState } from "../../../models/session/sessionState";
import { CkEditorDialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { archiveConfirmationWord, removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { Base } from "ui/Scripts/source/framework/base";
import { IAppReportEditItem } from "ui/Scripts/source/models/appReport/appReportEditItem";
import { AppReportTemplateFormModel, getAppReportTemplateFromSchema, IAppReportTemplateFormModel } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateFormModel";
import { AppReportEditDialogMenuItem, AppReportEditDialogModifyView, TAppReportEditDialogModifyViewRefProps } from "./AppReportEditDialogModifyView";
import { AppReportEditDialogReadOnlyPublishedView } from "./AppReportEditDialogReadOnlyPublishedView";

interface IAppReportEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    appReportId: string;
    newAppReport: IAppReportEditItem;
    newAppReportUnits: IIdTitleNode[];
    hasPrevious: boolean;
    hasNext: boolean;
    onSaved: (id: string) => void;
    onClose: () => void;
    onPrint: () => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
}

interface IAppReportEditDialogState {
    activeMenuItem: AppReportEditDialogMenuItem;
    isLoading: boolean;
    appReport: IAppReportEditItem;
    id: string;
    canEdit: boolean;
    canEditWithoutLimit: boolean;
    canRemove: boolean;
    units: IIdTitleNode[];
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
    //AppReportTemplate
    formDataRow: ITaskDataRow;
    appReportTemplate: IAppReportTemplateFormModel;
    formSelectedField: string;
    showSchemaDesignerDialog: boolean;
}

export const AppReportEditDialog = (props: IAppReportEditDialogProp) => {
    const _isMounted = useRef(false);
    // const orgStateHash = useRef("");
    const modifyViewRef = useRef<TAppReportEditDialogModifyViewRefProps>(null);

    const [state, setState] = useState<IAppReportEditDialogState>({
        activeMenuItem: AppReportEditDialogMenuItem.General,
        isLoading: false,
        appReport: null,
        id: "",
        canEdit: false,
        canEditWithoutLimit: false,
        canRemove: false,
        units: [],
        lockedUser: null,
        isLockedToOtherUser: false,
        //AppReportTemplate
        formDataRow: null,
        appReportTemplate: null,
        formSelectedField: "",
        showSchemaDesignerDialog: false,
    });

    const loadAppReport = async (id: string) => {
        const isNew = id === Base.emptyGuid && !state.appReport && !!props.newAppReport;
        //Unlock current appreport
        const unlockRecord = state.canEdit;
        if (unlockRecord && state.id && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.AppReport);
        }
        //Load and lock appreport
        setState((old) => ({ ...old, isLoading: true }));
        let appReport: IAppReportEditItem = null;
        let units: IIdTitleNode[] = [];
        let canEdit = false;
        let canEditWithoutLimit = false;
        let canRemove = false;
        if (!isNew) {
            const result = await AppUtils.callService(() => appReportService.getAppReportEdit(props.companyId, id, true), false, false, () => setState((old) => ({ ...old, isLoading: false })));
            if (!result) return;
            appReport = result.item;
            units = result.units;
            canEdit = result.canEdit;
            canEditWithoutLimit = result.canEditWithoutLimit;
            canRemove = result.canRemove;
        } else {
            appReport = props.newAppReport;
            units = props.newAppReportUnits;
            const result = await AppUtils.callService(() => appReportService.getAppReportEditUnitChange(props.companyId, appReport.unitId), false, false, () => setState((old) => ({ ...old, isLoading: false })));
            if (!result) return;
            canEdit = result.canEdit;
            canEditWithoutLimit = result.canEditWithoutLimit;
            canRemove = result.canRemove;
        }
        const parseResult = getAppReportTemplateFromSchema(appReport.dataSchema, state.appReportTemplate, state.formDataRow);
        AppReportTemplateFormModel.setTitlePageDataToFormDataRow(parseResult.appReportTemplate.formCols, parseResult.formDataRow, appReport.startDate, appReport.endDate);
        const newState = {
            ...state,
            isLoading: false,
            appReport,
            id: appReport.id,
            canEdit,
            canEditWithoutLimit,
            canRemove,
            units,
            lockedUser: appReport.lockedUser,
            isLockedToOtherUser: appReport.isLockedToOtherUser(props.sessionState.code),
            activeMenuItem: isNew
                ? AppReportEditDialogMenuItem.General
                : state.activeMenuItem,
            //AppReportTemplate
            appReportTemplate: parseResult.appReportTemplate,
            formDataRow: parseResult.formDataRow,
        };
        setState((old) => ({
            ...old,
            ...newState
        }));
        //Add locked record to idb
        if (!isNew && state.canEdit && LockedRecord.isLockedToUser(appReport.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, appReport.id, props.sessionState.code, DatabaseRecordType.AppReport));
        }
        //Send read receipt for appReport
        if (!isNew && !appReport.isRead) {
            const userIsCreator = appReport.createUser.code === props.sessionState.code;
            if (!userIsCreator) {
                AppUtils.callService(() => appReportService.sendAppReportReadReceipt(props.companyId, appReport.id), false, true);
            }
        }
    };

    const componentWillUnmount = useRef(false);

    useEffect(() => {
        _isMounted.current = true;
        
        return () => {
            _isMounted.current = false;
            componentWillUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            if (state.id && modifyViewRef.current) {
                const cancelResult = await AppUtils.cancel(modifyViewRef.current.getCurrentStateHash(), modifyViewRef.current.getOrgStateHash(), null);
                if (cancelResult === ConfirmationDialogResult.Cancel) return;
                if (cancelResult === ConfirmationDialogResult.Yes) {
                    if (!await modifyViewRef.current.saveToDb()) return;
                    await props.onRefreshList();
                }
            }
            await loadAppReport(props.appReportId);
        };

        loadData();
    }, [props.appReportId]);

    useEffect(() => {
        const unlockRecord = () => {
            const isNew = state.id === Base.emptyGuid;
            if (!isNew && state.canEdit && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
                recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.AppReport);
            }
        };

        return () => {
            if (componentWillUnmount.current) {
                unlockRecord();
            }
        };
    }, [state.id, state.canEdit, state.lockedUser]);

    const handleToggleActiveMenuItem = async (activeMenuItem: AppReportEditDialogMenuItem) => {
        if (state.activeMenuItem === activeMenuItem) return;
        if (state.activeMenuItem === AppReportEditDialogMenuItem.General) {
            if (!await modifyViewRef.current.validateBeforeSave()) return;
        }
        setState(old => ({ ...old, activeMenuItem }));
    };

    // #region Locking
    const handleUnlockAndLockToMe = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockReport, state.appReport.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await recordLockManager.lock(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.AppReport);
        if (!result || result.items.length < 1) return;
        setState((old) => ({
            ...old,
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        }));
    };
    // #endregion Locking

    const afterSaveToDb = (id: string) => {
        loadAppReport(id);
        props.onSaved(id);
    };

    const handleSave = async (closeDialog: boolean) => {
        const result = await modifyViewRef.current.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            afterSaveToDb(result.id);
            return;
        }
        props.onClose();
    };

    const handleRemove = async () => {
        if (state.isLockedToOtherUser || state.appReport.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveReportParameterPermanently, state.appReport.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => appReportService.removeAppReport(props.companyId, state.appReport.id));
            if (!result) return;
            setState(old => ({ ...old, lockedUser: null }));
            props.onClose();
        }
    };

    // #region Archive
    const handleArchive = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToArchiveAppReportParameter, state.appReport.code), false, archiveConfirmationWord) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await modifyViewRef.current.saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => appReportService.archiveAppReport(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };

    const handleRestoreFromArchive = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRestoreAppReportParameterFromArchive, state.appReport.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await modifyViewRef.current.saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => appReportService.restoreAppReportFromArchive(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };
    // #endregion Archive

    // #region Trash Bin
    const handleMoveToTrashBin = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToMoveReportParameterToTrashBin, state.appReport.code), false, removeConfirmationWord) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await modifyViewRef.current.saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => appReportService.moveAppReportToTrashBin(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };

    // const handleRestoreFromTrashBin = async () => {
    //     if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRestoreAppReportParameterFromATrashBin, state.appReport.code), false) !== ConfirmationDialogResult.Yes) {
    //         return;
    //     }
    //     const saveResult = await modifyViewRef.current.saveToDbIfChanged();
    //     if (!saveResult) return;
    //     const result = await AppUtils.callService(() => appReportService.restoreAppReportFromTrashBin(props.companyId, saveResult.id, saveResult.rowId));
    //     if (!result) return;
    //     afterSaveToDb(result.id);
    // };
    // #endregion Trash Bin

    const handlePrint = async () => {
        if (state.appReport.isNew()) return;
        props.onPrint();
    };

    const handleCancel = async () => {
        if (!modifyViewRef.current) {
            props.onClose();
            return;
        }
        const cancelResult = await AppUtils.cancel(modifyViewRef.current.getCurrentStateHash(), modifyViewRef.current.getOrgStateHash());
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(true);
        } else {
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        if (!modifyViewRef.current) return true;
        return await AppUtils.canMoveOut(modifyViewRef.current.getCurrentStateHash(), modifyViewRef.current.getOrgStateHash(),
            async () => !!await modifyViewRef.current.saveToDb(),
            props.onRefreshList,
            (newStateHash) => { modifyViewRef.current.setOrgStateHash(newStateHash); });
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    if (!state.appReport) return null;

    const isNew = state.appReport.isNew();
    const isArchived = !!state.appReport.archiveUser;
    const isInTrash = !!state.appReport.deleteUser;
    const isReadOnly = !(state.canEdit && !isArchived);
    const isReadOnlyPublishedView = !state.canEdit;

    const canArchive = !isNew && !state.isLockedToOtherUser && !isInTrash && !isArchived && state.canEdit;
    const canRestoreFromArchive = !isNew && !state.isLockedToOtherUser && !isInTrash && isArchived && state.canEdit;

    const canMoveToTrash = !isNew && !state.isLockedToOtherUser && !isInTrash && state.canRemove;
    //const canRestoreFromTrash = !isNew && !state.isLockedToOtherUser && isInTrash && state.canRemove;

    const canDeletePermanently = !isNew && !state.isLockedToOtherUser && isInTrash && state.canRemove;

    return (
        <CkEditorDialog
            id="appReportEditDialog"
            maxWidth="xl"
            maxHeight="100%"
            title={isNew
                ? Translations.AddReport
                : `${Translations.EditReport}: ${state.appReport.code}${(isArchived ? ", " + Translations.Archived : "")}${(isReadOnly ? ", " + Translations.ReadOnly : "")}${(state.isLockedToOtherUser ? ", " + Translations.Locked : "")}`
            }
            dialogTabs={!isReadOnlyPublishedView
                ? <DialogTabs
                    activeTabId={state.activeMenuItem}
                    tabs={[
                        { id: AppReportEditDialogMenuItem.General, title: Translations.BasicInformation },
                        { id: AppReportEditDialogMenuItem.Builder, title: Translations.ReportBuilder, hidden: !state.canEdit },
                        { id: AppReportEditDialogMenuItem.Preview, title: Translations.Preview }
                    ]}
                    onChangeTab={handleToggleActiveMenuItem}
                />
                : null
            }
            body={
                <>
                    {(isReadOnlyPublishedView
                        ? <AppReportEditDialogReadOnlyPublishedView
                            appReport={state.appReport}
                            units={state.units}
                        />
                        : <AppReportEditDialogModifyView
                            ref={modifyViewRef}
                            activeMenuItem={state.activeMenuItem}
                            sessionState={props.sessionState}
                            companyId={props.companyId}
                            appReport={state.appReport}
                            canEdit={state.canEdit}
                            canEditWithoutLimit={state.canEditWithoutLimit}
                            canRemove={state.canRemove}
                            units={state.units}
                            lockedUser={state.lockedUser}
                            isLockedToOtherUser={state.isLockedToOtherUser}
                            onAfterSaveToDb={afterSaveToDb}
                            onPrint={handlePrint}
                        />
                    )}
                </>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        hasPrevious={props.hasPrevious}
                        hasNext={props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        createUser={!isNew ? state.appReport.createUser : null}
                        modifyUser={!isNew && !isReadOnlyPublishedView ? state.appReport.modifyUser : null}
                        archiveUser={!isNew ? state.appReport.archiveUser : null}
                        publishUser={!isNew ? state.appReport.publishUser : null}
                        deleteUser={!isNew ? state.appReport.deleteUser : null}
                        lockedUser={state.canEdit ? state.lockedUser : null}
                        isLockedToOtherUser={!isNew && state.canEdit && state.isLockedToOtherUser}
                        onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? handleUnlockAndLockToMe : null}
                    />
                    <DialogButtons
                        isNew={isNew}
                        removeDisabled={state.isLockedToOtherUser || !state.canRemove}
                        saveDisabled={state.isLockedToOtherUser}
                        removeTitle={state.appReport.deleteUser ? Translations.RemoveReportPermanently : null}
                        onCancel={handleCancel}
                        onPrint={!isNew ? handlePrint : null}
                        onRemove={canDeletePermanently
                            ? handleRemove
                            : (canMoveToTrash
                                ? handleMoveToTrashBin
                                : null)}
                        onArchive={canArchive ? handleArchive : null}
                        onRestoreFromArchive={canRestoreFromArchive
                            ? handleRestoreFromArchive
                            : null
                        }
                        onSave={!isReadOnly ? () => handleSave(false) : null}
                    />
                </Box>
            }
            onClose={handleCancel}/>
    );
};